import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import styles from '../../css/admin/Header.module.css';
import { useLanguage } from '../../hooks/LanguageContext'; // Import useLanguage hook
import { isApp } from '../../utils/isApp'; // [APP-FORK]

export default function Header() {
  if (isApp()) return null; // [APP-FORK]
  //const { storeNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [pathname, setPathname] = useState('/');
  const [store, setStore] = useState(() => ({})); // 로컬스토리지에서 캐시된 상점 정보를 불러옴
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();

  const params = useParams();

  // URL에서 storeNo 추출 (login, giftstatus, verification, verifyconfirm, verifysuccess, verifyfailure, status 지원)
  const segments = location.pathname.split('/').filter(Boolean);
  const getNextOf = key => {
    const idx = segments.indexOf(key);
    return idx !== -1 && segments[idx + 1] ? segments[idx + 1] : null;
  };
  const queryStoreNo = new URLSearchParams(location.search).get('storeNo');
  const extractedStoreNo =
    getNextOf('login') ||
    getNextOf('giftstatus') ||
    getNextOf('verification') ||
    getNextOf('verifyconfirm') ||
    getNextOf('verifysuccess') ||
    getNextOf('verifyfailure') ||
    getNextOf('status') ||
    // [2026-11-04] /mobile/couponverify{confirm|success|failure}/{storeNo}/{쿠폰번호} — 뒤에 쿠폰번호가
    //  있을 때만(이전 주소 /…/{쿠폰번호} 는 상점번호가 아니다)
    ['couponverifyconfirm', 'couponverifysuccess', 'couponverifyfailure']
      .map(k => {
        const i = segments.indexOf(k);
        return i !== -1 && segments[i + 2] ? segments[i + 1] : null;
      })
      .find(Boolean);
  const storeNo =
    params.storeNo ||
    queryStoreNo ||
    extractedStoreNo ||
    localStorage.getItem('last_storeNo') ||
    '';

  const fetchStoreInfo = useCallback(async () => {
    //if (!store) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVER_PUBLIC}store/character/${storeNo}`
      );
      setStore(response.data.data);
      localStorage.setItem(
        `store_${storeNo}`,
        JSON.stringify(response.data.data)
      ); // 상점 정보를 로컬스토리지에 저장
      localStorage.setItem('last_storeNo', String(storeNo));
    } catch (error) {
      console.error('Error fetching store information:', error);
    }
  }, [storeNo]);
  // };
  useEffect(() => {
    setPathname(location.pathname);
  }, [location]);

  useEffect(() => {
    if (storeNo) {
      fetchStoreInfo();
    }
  }, [storeNo, fetchStoreInfo]);

  const renderHeader = () => {
    if (
      pathname === '/login' ||
      pathname === '/admin/login' ||
      pathname === '/checkout' ||
      pathname === '/mobile/login'
    ) {
      return;
    } else if (pathname === '/gift' || pathname.startsWith('/gift/')) {
      return (
        <div className={styles.giftHeader}>
          <div className={styles.giftHeaderFrame}>
            <div className={styles.giftHeaderText}>
              {language === 'ko' ? '선물하기' : 'Send Gift'}
            </div>
          </div>
        </div>
      );
    } else if (pathname === '/claim') {
      return (
        <div className={styles.claimHeader} onClick={() => navigate('/')}>
          <img className={styles.claimLogo} src="/images/logo.png" alt="logo" />
          <div className={styles.giftHeaderText}>
            {language === 'ko' ? '카페봄봄 기프트샵' : 'Cafe Bombom Gift Shop'}
          </div>
        </div>
      );
    } else {
      return (
        <>
          <div className={styles.header}>
            <button
              type="button"
              className={styles.hamburgerBtn}
              onClick={() => setIsMenuOpen(true)}
              aria-label="메뉴 열기"
            >
              <span className={styles.hamburgerLine} />
              <span className={styles.hamburgerLine} />
              <span className={styles.hamburgerLine} />
            </button>

            <div
              className={styles.containerText}
              onClick={() =>
                (window.location.href = `${process.env.REACT_APP_SHOPUSER_SERVER}${storeNo}`)
              }
              style={{ cursor: 'pointer' }}
            >
              {store ? `${store.storeName} 기프트샵` : null}
            </div>

            <div
              className={styles.logoWrap}
              onClick={() =>
                (window.location.href = `${process.env.REACT_APP_SHOPUSER_SERVER}${storeNo}`)
              }
            >
              <img
                className={styles.logo}
                src={
                  store && store.thumbnailFileUrl
                    ? store.thumbnailFileUrl
                    : 'images/no-image.png'
                }
                alt="naegift__logo"
              />
            </div>
          </div>

          {/* 사이드 메뉴 */}
          <div
            className={`${styles.sideMenuOverlay} ${isMenuOpen ? styles.sideMenuOpen : ''}`}
            onClick={() => setIsMenuOpen(false)}
            onKeyDown={e => e.key === 'Escape' && setIsMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="메뉴 닫기"
          />
          <div
            className={`${styles.sideMenu} ${isMenuOpen ? styles.sideMenuOpen : ''}`}
          >
            <div className={styles.sideMenuHeader}>
              <span className={styles.sideMenuTitle}>
                {language === 'ko' ? '메뉴' : 'Menu'}
              </span>
              <button
                type="button"
                className={styles.sideMenuClose}
                onClick={() => setIsMenuOpen(false)}
                aria-label="메뉴 닫기"
              >
                ×
              </button>
            </div>
            <div className={styles.sideMenuContent}>
              <div className={styles.sideMenuLanguageSection}>
                <span className={styles.sideMenuLanguageLabel}>
                  {language === 'ko' ? '언어' : 'Language'}
                </span>
                <div className={styles.sideMenuLanguageToggle}>
                  <button
                    type="button"
                    className={`${styles.sideMenuLangOption} ${language === 'ko' ? styles.sideMenuLangOptionActive : ''}`}
                    onClick={() => language !== 'ko' && toggleLanguage()}
                  >
                    한국어
                  </button>
                  <button
                    type="button"
                    className={`${styles.sideMenuLangOption} ${language === 'en' ? styles.sideMenuLangOptionActive : ''}`}
                    onClick={() => language !== 'en' && toggleLanguage()}
                  >
                    English
                  </button>
                </div>
              </div>
              <button
                type="button"
                className={styles.sideMenuItem}
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate(`/mobile/login/${storeNo}/normal`);
                }}
              >
                {language === 'ko' ? '상점관리자' : 'Store Admin'}
              </button>
            </div>
          </div>
        </>
      );
    }
  };

  return renderHeader();
}
